import React, { useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import { Typography, Spin, Card, Modal } from "antd";
import axios from "axios";
import { config } from "../../../../config";
import "./GaleriDesa.scss";
import PlayButton from "../../../../assets/images/youtubeVideo/play-button.png";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const { Title } = Typography;

const generateLowResPlaceholder = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      // Ukuran sangat kecil hanya untuk placeholder
      const size = 50;
      canvas.width = size;
      canvas.height = (img.height / img.width) * size;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.5));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

const CACHE_KEY = "galeri_desa_thumbs";
const CACHE_DURATION = 10 * 60 * 1000;

const GaleriDesa = () => {
  const [galeriDesaCantik, setGaleri] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const openModal = (item) => {
    setSelectedItem(item);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedItem(null);
  };

  const loadCache = () => {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return {};
    }
    return parsed.thumbs || {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const namaDomain = window.location.href;
        const res = await axios.get(
          `${config.BASE_URL}organization/findContentOrganization?namaDomain=${namaDomain}`,
        );

        const result = res.data?.data?.dokumentasiDesaCantik || [];

        // 1. Format data dasar
        const formatted = result.map((item) => ({
          id: item.dokumentasiDesaCantikId,
          keterangan: item.keterangan || "keterangan tidak tersedia",
          fullUrl: item.file
            ? `${config.BASE_URL}getReferenceFile?pathFile=${encodeURIComponent(item.file)}`
            : null,
          urlVideo: item.urlVideo || null,
          file: item.file || null,
        }));

        setGaleri(formatted);

        // 2. Load Cache (Instant)
        const cached = loadCache();
        setThumbnails(cached);
        setLoading(false); // Matikan loading utama segera setelah data teks ada

        // 3. Progressive Thumbnail Generation (Background Process)
        const newCache = { ...cached };
        for (const item of formatted) {
          // Jika belum ada di cache dan dia adalah gambar
          if (!newCache[item.id] && item.fullUrl) {
            const lowRes = await generateLowResPlaceholder(item.fullUrl);
            if (lowRes) {
              newCache[item.id] = lowRes;
              // Update UI per satu gambar
              setThumbnails((prev) => ({ ...prev, [item.id]: lowRes }));
            }
          }
        }

        // Simpan hasil update ke localStorage
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            timestamp: Date.now(),
            thumbs: newCache,
          }),
        );
      } catch (error) {
        console.error("Gagal ambil data Galeri:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const settings = {
    dots: false,
    infinite: true,
    autoplay: true,
    speed: 1000,
    autoplaySpeed: 5000,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      { breakpoint: 992, settings: { slidesToShow: 2 } },
      { breakpoint: 576, settings: { slidesToShow: 1 } },
    ],
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="desa-cantik-slider-wrapper">
      <div className="slider-header">
        <Title level={4} style={{ margin: 0 }}>
          Dokumentasi
        </Title>
        <div className="slider-arrows">
          <div
            className="arrow-button"
            onClick={() => sliderRef.current.slickPrev()}
          >
            <LeftOutlined />
          </div>
          <div
            className="arrow-button"
            onClick={() => sliderRef.current.slickNext()}
          >
            <RightOutlined />
          </div>
        </div>
      </div>

      <Slider ref={sliderRef} {...settings} className="desa-cantik-slider">
        {galeriDesaCantik.map((item, index) => {
          const cachedImg = thumbnails[item.id];
          const isVideo = !!item.urlVideo;

          // Logic Background
          let finalBg = "";
          if (item.file) {
            // Prioritas 1: Gambar asli. Fallback: Cache Low-res
            finalBg = `url(${item.fullUrl}), url(${cachedImg})`;
          } else if (isVideo) {
            const vidId = item.urlVideo.includes("v=")
              ? item.urlVideo.split("v=")[1]?.split("&")[0]
              : item.urlVideo;
            finalBg = `url(https://img.youtube.com/vi/${vidId}/hqdefault.jpg)`;
          } else {
            finalBg = `url(https://via.placeholder.com/800x400?text=No+Image)`;
          }

          return (
            <div key={index}>
              <Card
                bordered={false}
                className="carousel-card"
                style={{
                  backgroundImage: finalBg,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  cursor: "pointer",
                  backgroundColor: "#f0f0f0", // Warna dasar selagi load
                }}
                onClick={() => openModal(item)}
              >
                {isVideo && (
                  <div className="video-overlay">
                    <img src={PlayButton} alt="Play" className="play-icon" />
                  </div>
                )}
              </Card>
            </div>
          );
        })}
      </Slider>

      <Modal
        open={isModalVisible}
        onCancel={closeModal}
        footer={null}
        width={1200}
        centered
        className="image-modal"
      >
        {selectedItem && (
          <div className="modal-content-wrapper">
            <div className="modal-image">
              {selectedItem.file ? (
                <img
                  src={selectedItem.fullUrl}
                  alt="Preview"
                  style={{ width: "100%", borderRadius: 8 }}
                />
              ) : selectedItem.urlVideo ? (
                <iframe
                  width="100%"
                  height="500"
                  src={`https://www.youtube.com/embed/${
                    selectedItem.urlVideo.split("v=")[1]?.split("&")[0] ||
                    selectedItem.urlVideo
                  }`}
                  title="Video Preview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ borderRadius: 8 }}
                />
              ) : (
                <p>Tidak ada media.</p>
              )}
            </div>
            <div className="modal-description">
              <p>{selectedItem.keterangan}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GaleriDesa;
